/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import root from 'window-or-global';
import { useEffect, useRef } from 'react';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { useMediaQuery } from './useMediaQuery';
import {
  DESKTOP_FIXED_HEADER_HEIGHT,
  MOBILE_FIXED_HEADER_HEIGHT,
  TABLET_FIXED_HEADER_HEIGHT,
} from '@/features/header/constants/fixedHeaderHeights';
import { SCROLL_KEYS } from '@/constants/scrollKeys';

const MAX_ELEMENT_SEARCH_ATTEMPTS = 30;
const STABLE_SCROLL_THRESHOLD = 10;
const SCROLL_CHECK_INTERVAL_MS = 100;
const INITIAL_SCROLL_DELAY_MS = 300;
const SCROLL_POSITION_TOLERANCE = 10;

export const useScrollIntoViewDOM = () => {
  const router = useLegacyRouter();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery((theme) => theme.breakpoints.only('md'));
  const headerHeightRef = useRef(
    isMobile
      ? MOBILE_FIXED_HEADER_HEIGHT
      : isTablet
        ? TABLET_FIXED_HEADER_HEIGHT
        : DESKTOP_FIXED_HEADER_HEIGHT,
  );

  useEffect(() => {
    headerHeightRef.current = isMobile
      ? MOBILE_FIXED_HEADER_HEIGHT
      : isTablet
        ? TABLET_FIXED_HEADER_HEIGHT
        : DESKTOP_FIXED_HEADER_HEIGHT;
  }, [isMobile, isTablet]);

  useEffect(() => {
    const getHashValue = () => {
      if (typeof root === 'undefined') {
        return '';
      }

      try {
        const url = new URL(router.asPath, `${root.location?.origin || 'http://localhost'}`);

        if (url.hash) {
          return url.hash.substring(1);
        }

        return url.searchParams.get('id') || '';
      } catch {
        return '';
      }
    };

    const hash = getHashValue();
    if (!hash) {
      return;
    }

    let stableCount = 0;
    let userScrolled = false;
    let intervalId: NodeJS.Timeout;

    const handleUserScroll = () => {
      userScrolled = true;
      clearInterval(intervalId);
    };

    const handleUserKeyScroll = (e: KeyboardEvent) => {
      if (SCROLL_KEYS.has(e.key)) {
        userScrolled = true;
        clearInterval(intervalId);
      }
    };

    window.addEventListener('wheel', handleUserScroll, { passive: true, once: true });
    window.addEventListener('touchmove', handleUserScroll, { passive: true, once: true });
    window.addEventListener('keydown', handleUserKeyScroll, { once: true });

    const scrollToElement = () => {
      if (userScrolled) {
        return; // Break out if user scrolls away
      }

      const element = document.getElementById(hash);
      if (!element) {
        stableCount++;
        if (stableCount > MAX_ELEMENT_SEARCH_ATTEMPTS) {
          clearInterval(intervalId);
        }
        return;
      }

      const rect = element.getBoundingClientRect();
      const targetPosition = window.scrollY + rect.top;
      const currentScroll = window.scrollY;

      const adjustedTarget = targetPosition - headerHeightRef.current;

      if (Math.abs(currentScroll - adjustedTarget) > SCROLL_POSITION_TOLERANCE) {
        window.scrollTo({
          top: adjustedTarget,
          behavior: 'smooth',
        });
        stableCount = 0;
      } else {
        stableCount++;
      }

      if (stableCount >= STABLE_SCROLL_THRESHOLD) {
        clearInterval(intervalId);
      }
    };

    const timeout = setTimeout(() => {
      intervalId = setInterval(scrollToElement, SCROLL_CHECK_INTERVAL_MS);
    }, INITIAL_SCROLL_DELAY_MS);

    return () => {
      clearTimeout(timeout);
      if (intervalId) {
        clearInterval(intervalId);
      }
      window.removeEventListener('wheel', handleUserScroll);
      window.removeEventListener('touchmove', handleUserScroll);
      window.removeEventListener('keydown', handleUserKeyScroll);
    };
  }, [router.asPath]);
};
